import type { ProblemDocument } from '@axon/contracts/http';

/**
 * The one place this app talks to the API.
 *
 * The visitor token lives in a module variable here and is MIRRORED into
 * `sessionStorage` by `session-store.ts`. The reasoning for that, and why it
 * differs from the agent console's memory-only rule, is written where the mirror
 * is — this file only holds the live copy.
 */

// Indexing `import.meta.env` yields `any` — Vite types it as an index signature —
// so the value is narrowed here rather than trusted. An env var that is not a
// string means a misconfigured build, and falling back beats interpolating
// `[object Object]` into every request URL.
const CONFIGURED_BASE = import.meta.env['VITE_API_BASE_URL'] as unknown;
const BASE_URL: string =
  typeof CONFIGURED_BASE === 'string' && CONFIGURED_BASE !== ''
    ? CONFIGURED_BASE
    : 'http://127.0.0.1:3000';

let visitorToken: string | null = null;

export function setVisitorToken(token: string | null): void {
  visitorToken = token;
}

export function hasVisitorToken(): boolean {
  return visitorToken !== null;
}

/** A failure the API described in RFC 9457 terms. Anything else is ApiTransportError. */
export class ApiProblemError extends Error {
  constructor(readonly problem: ProblemDocument) {
    super(problem.detail);
    this.name = 'ApiProblemError';
  }

  get status(): number {
    return this.problem.status;
  }

  get code(): string {
    return this.problem.code;
  }
}

/** The network failed, or the response was not a problem document we can read. */
export class ApiTransportError extends Error {
  constructor(
    message: string,
    override readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'ApiTransportError';
  }
}

export interface RequestOptions {
  /**
   * `PUT` is here for `widget.context.put` and for nothing else. The union is
   * still closed rather than widened to `string`: this app talks to four routes
   * and a typo in a verb is a 405 a visitor sees, not a compile error somebody
   * fixes. `PATCH` and `DELETE` are deliberately absent — no visitor route uses
   * either, and adding one to the type is how the first one gets written.
   */
  readonly method?: 'GET' | 'POST' | 'PUT';
  readonly body?: unknown;
  /** Send no Authorization header even when a token is held (the bootstrap). */
  readonly anonymous?: boolean;
  /**
   * Send and accept cookies on an AUTHORISED call. One route needs it: ending
   * the session (ADR-0249 §4), which must present the continuity cookie to
   * revoke it and receive the `Max-Age=0` that deletes it.
   */
  readonly cookies?: boolean;
  readonly signal?: AbortSignal;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (options.body !== undefined) {
    headers['content-type'] = 'application/json';
  }
  if (visitorToken !== null && options.anonymous !== true) {
    headers['authorization'] = `Bearer ${visitorToken}`;
  }

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method: options.method ?? 'GET',
      headers,
      // 'include' ON THE BOOTSTRAP, 'omit' EVERYWHERE ELSE — and the split is the
      // point. This app's authority is the bearer token above, which is why the
      // contract returns the visitor token in the body rather than as a cookie,
      // and why the authorised routes send no cookies at all: a cookie riding on
      // a cross-origin POST is ambient authority, and ambient authority is the
      // precondition for CSRF. The bootstrap is the one exception, and P2-29 made
      // it one: the server answers it with `__Host-omni_widget_continuity`, the
      // 30-day httpOnly credential that resumes the SAME person on the next
      // visit — which is what makes a lead's history (P2-52) exist at all. With
      // `omit` here the browser neither stored nor sent that cookie, so every
      // visit minted a new person and the history was always one conversation
      // long; that is how the widget shipped from P2-29 until this line changed.
      // What the cookie grants is bounded by the server: it is read by the
      // bootstrap only, resolves to a person, and confers nothing on a page that
      // cannot read the response (CORS allowlist, `Access-Control-Allow-Credentials`).
      //
      // The sign-out is the second exception (ADR-0249 §4), and it is the same
      // cookie: forgetting the device means presenting it once more so the
      // server can revoke it, and accepting the `Max-Age=0` that deletes it.
      credentials: options.anonymous === true || options.cookies === true ? 'include' : 'omit',
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    });
  } catch (cause) {
    throw new ApiTransportError('The API could not be reached.', cause);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();

  if (!response.ok) {
    // Every failure from this API is application/problem+json — that is the
    // contract the server holds up, including for routes that do not exist. If
    // this parse fails, something between us and the API answered instead.
    try {
      throw new ApiProblemError(JSON.parse(text) as ProblemDocument);
    } catch (error) {
      if (error instanceof ApiProblemError) {
        throw error;
      }
      throw new ApiTransportError(
        `The API answered ${String(response.status)} with a body that is not a problem document.`,
        error,
      );
    }
  }

  try {
    return JSON.parse(text) as T;
  } catch (cause) {
    throw new ApiTransportError('The API answered with a body that is not JSON.', cause);
  }
}
